'use client';

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { ArrowRight, ArrowUpRight, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { organizeHomepageLanding, sceneVideoPresentsMessaging, VISITOR_LEGACY_POSTER, VISITOR_LEGACY_VIDEO, type HomepageExperience as Content } from '@telegamez/types';
import { useAuthSheet } from '@/context/AuthSheetContext';
import { useHomepageAnalytics } from '@/hooks/useHomepageAnalytics';
import { useHomepageVideoOrientation } from '@/hooks/useHomepageVideoOrientation';
import { selectHomepageVideo } from '@/lib/homepage-video';
import { VisitorSiteMenu } from './visitor-room/VisitorSiteMenu';
import home from './home-page.module.css';

const VisitorRoom = dynamic(() => import('./visitor-room/VisitorRoom').then(module => module.VisitorRoom), {
  loading: () => <div className={home.loading} role="status">Opening your room…</div>,
});
const FEATURES = { overview: undefined, creator: 'programs', sports: 'media', fam: 'activities' } as const;
/**
 * #4893 — each audience story's Start button opens Create room on the matching purpose, so the
 * value someone saw in the clip is the first screen they land on. A path, not a query, so it
 * survives the sign-in redirect.
 */
export const AUDIENCE_START = {
  overview: null,
  creator: { experience: 'show', label: 'Start your show' },
  sports: { experience: 'game_day', label: 'Start a game day room' },
  fam: { experience: 'game_night', label: 'Start a game night room' },
} as const;
interface Props { content: Content; isLoggedIn?: boolean; preview?: boolean }

export function HomepageExperience({ content, isLoggedIn = false, preview = false }: Props) {
  const [visiting, setVisiting] = useState(false);
  const { track } = useHomepageAnalytics(preview);
  // Unmount and stop the landing video before loading any visitor-room hooks.
  return visiting ? <VisitorRoom content={content} isLoggedIn={isLoggedIn} preview={preview} trackPageView={false} onReturnToLanding={() => setVisiting(false)} />
    : <HomeLanding content={content} isLoggedIn={isLoggedIn} track={track} onVisit={() => { track('control', { action: 'open', source: 'entry' }); setVisiting(true); }} />;
}

function HomeLanding({ content, isLoggedIn, track, onVisit }: { content: Content; isLoggedIn: boolean; track: ReturnType<typeof useHomepageAnalytics>['track']; onVisit: () => void }) {
  const { openSheet, isSheetOpen } = useAuthSheet();
  const [world, setWorld] = useState(0);
  const [messageIndex, setMessageIndex] = useState(-1);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const portrait = useHomepageVideoOrientation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const loadedSource = useRef('');
  const playhead = useRef({ clip: '', time: 0 });
  const landing = useMemo(() => organizeHomepageLanding(content.landing), [content.landing]);
  const section = landing.scenes[world];
  // This section's clip carries the messaging: no overlay copy, message buttons, blur or shade.
  const videoMessaging = sceneVideoPresentsMessaging(landing, section);
  const current = videoMessaging || messageIndex < 0 ? section : section.messages![messageIndex];
  const audience = section.audience!;
  const sectionMedia = current.videoUrl || current.portraitVideoUrl ? current : section;
  const customSceneVideo = Boolean(sectionMedia.videoUrl || sectionMedia.portraitVideoUrl);
  const customCommercial = Boolean(content.commercial.videoUrl || content.commercial.portraitVideoUrl);
  const media = customSceneVideo ? sectionMedia : customCommercial
    ? { ...content.commercial, videoPoster: content.commercial.poster }
    : { videoUrl: VISITOR_LEGACY_VIDEO, videoPoster: content.commercial.poster || VISITOR_LEGACY_POSTER };
  const preferred = selectHomepageVideo(media, portrait ?? false);
  const alternate = selectHomepageVideo(media, !(portrait ?? false));
  const selected = failedSources.includes(preferred.src) ? alternate : preferred;
  const { src, poster } = selected;
  const clip = `${media.videoUrl}|${'portraitVideoUrl' in media ? media.portraitVideoUrl : ''}`;
  const hasVideo = Boolean(src && !failedSources.includes(src));
  const caption = (customSceneVideo ? sectionMedia.videoCaption : content.commercial.caption) || current.label;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || portrait === null) return;
    let active = true;
    if (paused) video.pause();
    else void video.play().catch(() => { if (active) setPaused(true); });
    return () => { active = false; video.pause(); };
  }, [src, paused, hasVideo, portrait]);

  return <div className={home.home} data-world={world} data-video-messaging={videoMessaging || undefined} style={{ '--video-blur': `${videoMessaging ? 0 : landing.videoBlur}px`, '--overlay-opacity': videoMessaging ? 0 : landing.overlayOpacity } as CSSProperties}>
    <header className={home.header}>
      <VisitorSiteMenu onOpen={() => track('menu_open', { source: 'header' })} onNavigate={destination => track('nav_click', { source: 'site_menu', destination })} />
      <Link href="/" className={home.brand} aria-label="Telegames homepage"><img src="/images/logo192.png" alt="" width={34} height={34} /><span>telegames<span className={home.brandDot}>.</span></span></Link>
      <span className={home.headerNote}>{landing.eyebrow}</span>
      <div className={home.headerActions}>
        {isLoggedIn ? <Link href="/dashboard" onClick={() => track('cta_click', { source: 'header', destination: 'dashboard' })}>Your rooms <ArrowUpRight size={15} /></Link>
          : <button type="button" aria-haspopup="dialog" aria-expanded={isSheetOpen} onClick={() => { track('login_click', { source: 'header' }); openSheet('login', { redirectTo: '/dashboard' }); }}>Sign in <ArrowUpRight size={15} /></button>}
      </div>
    </header>
    <main>
      <nav className={home.worldNav} data-scene-count={landing.scenes.length} style={{ '--landing-scene-count': landing.scenes.length } as CSSProperties} aria-label="Explore ways to use Telegames">
        {landing.scenes.map((item, index) => <button type="button" key={item.audience} aria-pressed={world === index} onClick={() => { setWorld(index); setMessageIndex(-1); track('chapter_view', { audience: item.audience, feature: FEATURES[item.audience!], chapter_index: index + 1, source: 'manual' }); }}>
          <span className={home.navIndex}>0{index + 1}</span><span><strong>{item.label}</strong><small>{item.navDescription}</small></span><ArrowUpRight size={20} />
        </button>)}
      </nav>
      <section className={home.hero} aria-label="Telegames in action">
        <div className={home.videoLayer}>
          {hasVideo ? <video ref={videoRef} src={portrait === null ? undefined : src} poster={poster || undefined} className={home.backgroundVideo} aria-label={caption} autoPlay loop muted={muted} playsInline
            onTimeUpdate={event => { if (loadedSource.current === src && event.currentTarget.readyState >= 1) playhead.current = { clip, time: event.currentTarget.currentTime }; }}
            onLoadedMetadata={event => {
              const video = event.currentTarget;
              loadedSource.current = src;
              if (playhead.current.clip === clip && Number.isFinite(video.duration)) video.currentTime = Math.min(playhead.current.time, Math.max(0, video.duration - 0.1));
            }}
            onPlay={() => track('video', { action: 'play', source: 'video_player' })}
            onPause={() => track('video', { action: 'pause', source: 'video_player' })}
            onError={() => { setFailedSources(values => [...values, src]); track('video', { action: 'error', source: 'video_player' }); }} />
            : <div className={home.videoFallback} role="status">The preview video is unavailable. You can still explore with Tele.</div>}
        </div>
        <div className={home.shade} aria-hidden="true" />
        <div className={home.edgeLight} aria-hidden="true" />
        <div className={home.heroTop}>
          {landing.releaseLabel && <span className={home.release}>{landing.releaseLabel}</span>}
          <span className={home.sequence}>0{world + 1} / {String(landing.scenes.length).padStart(2, '0')}</span>
        </div>
        <div className={home.heroBottom}>
          {videoMessaging ? <h1 id="home-heading" className={home.srOnly} aria-live="polite">{current.title} {current.emphasis}</h1> : <div className={home.message}>
            {section.messages && <nav className={home.messageNav} aria-label={`${section.label} messages`}>
              {[{ label: 'Overview' }, ...section.messages].map((message, index) => <button type="button" key={index} aria-pressed={messageIndex === index - 1} onClick={() => {
                setMessageIndex(index - 1);
                track('chapter_view', { audience, feature: FEATURES[audience], chapter_index: world + 1, ...(index > 0 ? { message_index: index } : {}), source: 'manual' });
              }}>{message.label}</button>)}
            </nav>}
            <div aria-live="polite" aria-atomic="true"><div key={`${world}:${messageIndex}`} className={home.copy}>
              <p className={home.chapter}><span>0{world + 1}</span>{current.label}</p>
              <h1 id="home-heading">{current.title}<br /><em>{current.emphasis}</em></h1>
              <p className={home.description}>{current.description}</p>
              {current.detail && <p className={home.detail}>{current.detail}</p>}
            </div></div>
          </div>}
          {/* #4893: outside the copy, so it shows when the video carries the messaging too. */}
          {AUDIENCE_START[audience] && (() => {
            const start = AUDIENCE_START[audience]!;
            const href = `/dashboard/create/${start.experience}`;
            const onStart = () => track('cta_click', { source: 'manual', destination: 'dashboard', audience });
            return isLoggedIn
              ? <Link href={href} className={home.audienceStart} onClick={onStart}>{start.label}<ArrowRight size={17} /></Link>
              : <button type="button" className={home.audienceStart} onClick={() => { onStart(); openSheet('login', { redirectTo: href }); }}>{start.label}<ArrowRight size={17} /></button>;
          })()}
          <div className={home.controlsColumn}>
            <span className={home.caption}>{caption}</span>
            <div className={home.videoControls}>
              {hasVideo && <div className={home.backgroundControls} role="group" aria-label="Background video">
                <button type="button" aria-label={paused ? 'Play background video' : 'Pause background video'} onClick={() => setPaused(value => !value)}>{paused ? <Play size={17} /> : <Pause size={17} />}</button>
                <button type="button" aria-label={muted ? 'Unmute background video' : 'Mute background video'} aria-pressed={!muted} onClick={() => { setMuted(value => !value); track('control', { action: muted ? 'unmute' : 'mute', source: 'video_player' }); }}>{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}<span>{muted ? 'Sound off' : 'Sound on'}</span></button>
              </div>}
            </div>
          </div>
        </div>
      </section>
      <div className={home.visitStrip}>
        <span>{landing.ctaTitle}<small>{landing.ctaNote}</small></span>
        <button type="button" onClick={onVisit}>{landing.ctaLabel}<ArrowRight size={17} /></button>
      </div>
    </main>
  </div>;
}
